import HBIcon from "@/components/HBIcon";
import { getPlusActivity } from "@/services";
import useReq from "@/utils/useReq";
import { Tooltip } from "antd";
import classNames from "classnames";
import { useState } from "react";
import Swiper from "../shared/swiper";
import VipCheckbox from "../vipCheckbox/checkBox";
import styles from "./plusActivity.module.less";

interface IPlusSkuExtend extends IPlusSku {
  config?: {
    code: string;
    skuId: string;
    name: string;
    desc: string;
    exchangeHref: string;
    protocols?: {
      name: string;
      href: string;
    }[];
  };
}

interface PlusActivityModuleProps {
  isCompany?: boolean;
  goods: IPlusSkuExtend[];
  defaultSelect: string;
  selected: IPlusSkuExtend[];
  onSelect: (good: IPlusSkuExtend[]) => void;
  config: {
    name: string;
    desc: string;
    goods: {
      code: string;
      skuId: string;
      name: string;
      desc: string;
      exchangeHref: string;
      protocols?: {
        name: string;
        href: string;
      }[];
    }[];
  };
}

interface IUsePlusActivityProps {
  current?: any;
  defaultSelectPlusSku?: string;
  config?: {
    name: string;
    desc: string;
    goods: {
      code: string;
      skuId: string;
      name: string;
      desc: string;
      exchangeHref: string;
      protocols?: {
        name: string;
        href: string;
      }[];
    }[];
  };
}

export function usePlusActivity({
  current,
  defaultSelectPlusSku,
  config,
}: IUsePlusActivityProps) {
  // 换购活动
  const [selectedPlusSku, setSelectedPlusSku] = useState<IPlusSkuExtend[]>([]);
  const { data: plusActivity } = useReq(
    async () => {
      if (current?.plusActivity) {
        // 支持多个活动code
        const codes = current.plusActivity.split(",");
        const res = await getPlusActivity(codes);
        if (res.err) return;

        let skus: IPlusSkuExtend[] = [];
        codes.forEach((code) => {
          if (res[code].check.can_join && res[code].activity_plus_buys) {
            res[code].activity_plus_buys.forEach((item) => {
              skus = skus.concat(
                item.plus_sku.map((sku) => {
                  return {
                    ...sku,
                    activity: { activityId: item.activity.id },
                  };
                })
              );
            });
          }
        });

        // 与配置一一对应,并筛选出有配置的商品
        skus = skus.filter((good) => {
          const code = [good.sku.goods.code, good.sku.custom_code]
            .filter((item) => item)
            .join(".");

          // 两种匹配方式
          // 1、code 商品code.自定义Skucode
          // 2、skuId 匹配
          const matchConf = config?.goods?.find(
            (item) => item.code === code || +item.code == good.sku.id
          );

          if (matchConf) {
            good.config = matchConf;
            return true;
          }
        });

        // 默认选中的指定的换购商品
        if (defaultSelectPlusSku) {
          const findSku = skus.find((good) => {
            return (
              good.config?.code === defaultSelectPlusSku ||
              good.sku.id === +defaultSelectPlusSku
            );
          });
          if (findSku) setSelectedPlusSku([findSku]);
        } else {
          const freeSku = skus.find((item) => item.price === 0);
          // 默认选中免费的换购商品
          if (freeSku) {
            setSelectedPlusSku([freeSku]);
          } else {
            setSelectedPlusSku([]);
          }
        }

        return {
          skus,
        };
      }
    },
    {
      refreshDeps: [current],
    }
  );

  return {
    selectedPlusSku,
    setSelectedPlusSku,
    plusActivity,
  };
}

/**
 * 加价换购模块
 * 设计稿：https://www.figma.com/file/EihPzwEMK5G1trXGF4yDMJ/%E8%8A%B1%E7%93%A3%E5%95%86%E4%B8%9A%E5%8C%96?node-id=18010%3A113158&mode=dev
 */
export function PlusActivityModule({
  isCompany,
  goods,
  selected,
  onSelect,
  config,
}: PlusActivityModuleProps) {
  const toogleSelect = (good: IPlusSkuExtend) => {
    if (selected.some((item) => item.sku.id === good.sku.id)) {
      onSelect(selected.filter((item) => item.sku.id !== good.sku.id));
    } else {
      onSelect([...selected, good]);
    }
  };

  return (
    <div className={classNames(styles.mod, { [styles.company]: isCompany })}>
      <div className={styles.modName}>
        {config?.name}
        <Tooltip
          overlayClassName={styles.tooltip}
          title={<div dangerouslySetInnerHTML={{ __html: config?.desc }}></div>}
          placement={"topLeft"}
          align={{ offset: [-8, 0] }}
        >
          <HBIcon type="ic_info" />
        </Tooltip>
      </div>
      <Swiper width={708} calibration={-107} className={styles.packageList}>
        {goods.map((good) => {
          return (
            <div
              key={good.sku.id}
              className={classNames(styles.package, {
                // [styles.disabled]: disabled,
                [styles.active]: selected?.some(
                  (item) => item.sku.id === good.sku.id
                ),
              })}
              onClick={() => {
                toogleSelect(good);
              }}
            >
              <VipCheckbox
                checked={selected?.some((item) => item.sku.id === good.sku.id)}
              />
              <div className={styles.info}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: good.config.name || good.sku.name,
                  }}
                ></div>
                <div
                  dangerouslySetInnerHTML={{
                    __html: good.config.desc,
                  }}
                ></div>
              </div>
              <div className={styles.price}>
                ¥<span>{good.price}</span>
                <span className={styles.origin}>￥{good.sku.price}</span>
              </div>
            </div>
          );
        })}
      </Swiper>
    </div>
  );
}
